import angular from 'angular'

import {
  has, keys, wrap, clone, assign,
  toPairs, isEqual, isArray, isObject,
  isString, includes, zipObject, cloneDeep,
  isBoolean, isPlainObject
} from 'lodash-es'

import { includesAll } from '../utils/arrayh'
import { err2str, isEmpty, isGtZeroInteger, isNotEmptyObject } from '../utils/bits'

import envRaw from '../utils/env-raw'
import browser from '../utils/browser'
import Emitter from '../utils/emitter'
import topDiff from '../utils/top-diff'
import StrictObj from '../utils/strict-obj'
import debugFields from '../utils/debug-fields'
import LockableObj from '../utils/lockable-obj'
import attachGetters from '../utils/attach-getters'
import generateModulus from '../utils/generate-modulus'
import isFixedPositionBroken from '../utils/is-fixed-position-broken'

import DEFAULT_LOGO from '../../img/default-logo.svg'

import EMPTY_IMAGE_DARK from '../../img/empty-icons/dark.svg'
import EMPTY_IMAGE_BRIGHT from '../../img/empty-icons/white.svg'

import CAROUSEL_BUTTON_LEFT_DARK from '../../img/carousel-buttons/dark-left.svg'
import CAROUSEL_BUTTON_LEFT_BRIGHT from '../../img/carousel-buttons/bright-left.svg'
import CAROUSEL_BUTTON_RIGHT_DARK from '../../img/carousel-buttons/dark-right.svg'
import CAROUSEL_BUTTON_RIGHT_BRIGHT from '../../img/carousel-buttons/bright-right.svg'

import {
  RANDOM_ORDER,
  RECENT_ORDER,
  POPULAR_ORDER
} from '../utils/shared-constants'

import {
  Equal, Replace, ColorSchema, TrueIf,
  FalseIf, DropIf, ValueIf, TrueUnless,
  FalseUnless, DropUnless, ValueUnless
} from './gallery-properties.flow.helpers'

import Flow from './gallery-properties.flow'

import {
  castUrl, castSrc, castCss, castHex,
  castHtml, castText, castBool, castLower,
  castPosInt, castOpacity, castPosFloat, castFeatures,
  castPosOddInt, castCustomPlan, castCampaignKey, castMessageBundle
} from './gallery-properties.flow.step.cast.js'

import {
  OverrideByKey as VK,
  OverrideByEnv as VE,
  OverrideByConst as VC
} from './gallery-properties.flow.step.override'

import {
  FallbackToMessageBundle as FMB,
  FallbackToKey as FK,
  FallbackToEnv as FE,
  FallbackToConst as FC
} from './gallery-properties.flow.step.fallback'

const orNull = FC(DropIf(isEmpty))
const orTrue = FC(TrueUnless(isBoolean))
const orFalse = FC(FalseUnless(isBoolean))
const nullIfLteZero = FC(DropUnless(isGtZeroInteger))

import CalcModalsUse from './gallery-properties.flow.step.user.modals-use'
import calcLocaleDir from './gallery-properties.flow.step.user.locale-dir'
import calcShowVoteCount from './gallery-properties.flow.step.user.show-vote-count'
import calcTopPostsLimit from './gallery-properties.flow.step.user.top-posts-limit'
import calcVoteFramePath from './gallery-properties.flow.step.user.vote-frame-path'
import calcAbovePostsLimit from './gallery-properties.flow.step.user.above-posts-limit'
import calcBelowPostsLimit from './gallery-properties.flow.step.user.below-posts-limit'
import calcHighlightAllPosts from './gallery-properties.flow.step.user.highlight-all-posts'
import calcUseRawImagesAsThumbs from './gallery-properties.flow.step.user.use-raw-images-as-thumbs'
import calcCompleteMessageBundle from './gallery-properties.flow.step.user.complete-message-bundle'
import calcHighlightSeveralPosts from './gallery-properties.flow.step.user.highlight-several-posts'
import calcModalShowBrandingFooter from './gallery-properties.flow.step.user.modal-show-branding-footer'
import calcCarouselScrollbarOverlay from './gallery-properties.flow.step.user.carousel-scrollbar-overlay'
import calcHeaderContentMetaOpacity from './gallery-properties.flow.step.user.header-content-meta-opacity'
import calcHeaderContentMetaBackground from './gallery-properties.flow.step.user.header-content-meta-background'

angular
  .module('ggs.properties')
  .factory('Properties', PropertiesProvider)
  .factory('isProperties', isPropertiesProvider)

function isPropertiesProvider(debugObj) {
  return debugObj('isProperties', isProperties)
  function isProperties(input) {
    return true
      && isArray(input.keys)
      && isPlainObject(input.ovr)
      && isPlainObject(input.raw)
      && includesAll(keys(input), input.keys)
  }
}

function PropertiesProvider(
  env,
  $log,
  debugObj,
  isProperties,
  $portalElement,
  printPropertiesDiff) {

  return debugObj('Properties', Properties)

  function Properties(input) {
    const key2fix = {
      key:                               Replace(env.props.galleryKey),
      app_name:                          Flow(castText, FC('Love')),
      max_id:                            Flow(castPosInt, orNull),
      modulus:                           Flow(castPosInt, FC(generateModulus())),
      features:                          Flow(castFeatures, orNull),
      custom_styles:                     Flow(castCss, orNull),
      private_entries:                   Flow(castBool, orFalse),
      shared_post_key:                   Replace(env.props.sharedPostKey),
      shared_slide_index:                Replace(env.props.sharedSlideIndex),
      capture_integration:               Flow(castBool, orFalse),
      allow_fetch_above_posts:           Flow(castBool, VK(TrueIf('presentation_mode'), FalseUnless('recent_order')), orFalse),
      allow_overflow_y_change:           Flow(castBool, VE(env.props.allowOverflowYChange), orTrue),
      // locale
      locale:                            Flow(castText, VE(env.props.locale), FC('en')),
      locale_dir:                        Flow(castLower, calcLocaleDir, orNull),
      all_message_bundles:               Flow(castMessageBundle, orNull),
      message_bundle_overrides:          Flow(castMessageBundle, orNull),
      complete_message_bundle:           Flow(castMessageBundle, calcCompleteMessageBundle, orNull),
      // voting
      allow_voting:                      Flow(castBool, orFalse),
      has_votes:                         Flow(castBool, orFalse),
      show_vote_count:                   Flow(castBool, calcShowVoteCount, orFalse),
      use_mock_vote_frame:               Flow(castBool, FE(envRaw.liveEditMode), orFalse),
      vote_frame_path:                   Flow(castUrl, calcVoteFramePath, orNull),
      contestant_login:                  Flow(castBool, orFalse),
      // sorting
      hide_sorting:                      Flow(castBool, orFalse),
      allow_sorting:                     Flow(castBool, VK(FalseIf('hide_sorting')), VE(env.props.allowSorting), FK('show_vote_count'), orFalse),
      order_type:                        Flow(castLower, FE(env.props.initOrderType), FC(POPULAR_ORDER)),
      random_order:                      Equal('order_type', RANDOM_ORDER),
      recent_order:                      Equal('order_type', RECENT_ORDER),
      popular_order:                     Equal('order_type', POPULAR_ORDER),
      sort_text_recent:                  Flow(castText, FMB('gallery_most_recent'), orNull),
      sort_text_popular:                 Flow(castText, FMB('gallery_most_popular'), orNull),
      sort_text_random:                  Flow(castText, FMB('gallery_random'), orNull),
      order_selector_current_color:      Flow(castHex, FK(ColorSchema('body_background_color', 'body_bg_color_opacity', '#ffffff', '#575E65')), orNull),
      // sharing
      allow_sharing:                     Flow(castBool, orFalse),
      facebook_tab:                      Flow(castBool, orFalse),
      root_location:                     Flow(castUrl, VE(env.props.rootLocation), FC(window.location.href)),
      force_hosted_share_link:           Flow(castBool, VE(env.props.forceHostedShareLink), orFalse),
      force_embedded_share_link:         Flow(castBool, VE(env.props.forceEmbeddedShareLink), orFalse),
      sharing_tooltip_force_tiny:        Flow(castBool, orFalse),
      code_wizard_force_tiny:            Flow(castBool, orFalse),
      // branding footer
      branding_footer_force_tiny:        Flow(castBool, orFalse),
      branding_footer_force_show:        Flow(castBool, VK(TrueIf('presentation_auto_control')), VC(TrueIf(browser.likeMobile)), orFalse),
      branding_footer_color:             Flow(castHex, FK(ColorSchema('modals_bg_desktop_color', '#ffffff', '#32383d')), orNull),
      // presentation mode
      allow_presentation_mode:           Flow(castBool, VC(FalseUnless(env.isHostedMode)), orFalse),
      presentation_mode:                 Flow(castBool, VK(FalseUnless('allow_presentation_mode')), orFalse),
      presentation_control_type:         Flow(castLower, VK(DropUnless('presentation_mode')), FK(ValueIf('presentation_mode', 'auto')), orNull),
      presentation_auto_control:         Equal('presentation_control_type', 'auto'),
      presentation_manual_control:       Equal('presentation_control_type', 'manual'),
      presentation_tick_interval:        Flow(castPosInt, VK(DropUnless('presentation_auto_control')), FK(ValueIf('presentation_auto_control', 10e3)), orNull),
      // header
      allow_header:                      Flow(castBool, VE(env.props.allowHeader), orTrue),
      // header cover
      header_show_cover:                 Flow(castBool, orFalse),
      cover_solid_height:                Flow(castPosInt, orNull),
      cover_image_height:                Flow(castPosInt, orNull),
      cover_gradient_height:             Flow(castPosInt, orNull),
      image:                             Flow(castSrc, orNull),
      image_opacity:                     Flow(castOpacity, FC(1.0)),
      image_background:                  Flow(castHex, FC('#f3f6f9')),
      image_bg_opacity:                  Flow(castOpacity, FC(1.0)),
      header_background:                 Flow(castHex, FC('#5758cf')),
      header_background_2:               Flow(castHex, FC('#569dd2')),
      header_bg_1_opacity:               Flow(castOpacity, FC(1.0)),
      header_bg_2_opacity:               Flow(castOpacity, FC(1.0)),
      header_cover_bg_type:              Flow(castLower, FC('solid')),
      header_solid_background:           Flow(castHex, FC('#569dd2')),
      header_solid_bg_opacity:           Flow(castOpacity, FC(1.0)),
      header_gradient_direction:         Flow(castLower, FC('to right')),
      // header logo
      header_show_logo:                  Flow(castBool, orFalse),
      logo:                              Flow(castSrc, FC(DEFAULT_LOGO)),
      logo_stroke:                       Flow(castBool, orTrue),
      logo_opacity:                      Flow(castOpacity, FC(1.0)),
      logo_background:                   Flow(castHex, FC('#f3f6f9')),
      logo_bg_opacity:                   Flow(castOpacity, FC(1.0)),
      // header text
      header_display_name:               Flow(castHtml, orNull),
      header_show_text:                  Flow(castBool, orFalse),
      header_color:                      Flow(castHex, FK(ColorSchema('header_content_meta_background', 'header_content_meta_opacity', '#ffffff', '#32383d')), orNull),
      description:                       Flow(castHtml, orNull),
      description_color:                 Flow(castHex, FK(ColorSchema('header_content_meta_background', 'header_content_meta_opacity', '#ffffff', '#6c7d8c')), orNull),
      description_links_color:           Flow(castHex, FK(ColorSchema('header_content_meta_background', 'header_content_meta_opacity', '#ffffff', '#337ab7')), orNull),
      description_links_decoration:      Flow(castLower, FK(ColorSchema('header_content_meta_background', 'header_content_meta_opacity', 'underline', null)), orNull),
      header_content_max_width:          Flow(castPosInt, FC(600)),
      // header urls
      header_show_urls:                  Flow(castBool, orFalse),
      header_icons_color:                Flow(castHex, FK(ColorSchema('header_content_meta_background', 'header_content_meta_opacity', '#ffffff', null)), orNull),
      facebook_url:                      Flow(castUrl, orNull),
      twitter_url:                       Flow(castUrl, orNull),
      instagram_url:                     Flow(castUrl, orNull),
      tumblr_url:                        Flow(castUrl, orNull),
      youtube_url:                       Flow(castUrl, orNull),
      soundcloud_url:                    Flow(castUrl, orNull),
      pinterest_url:                     Flow(castUrl, orNull),
      website_url:                       Flow(castUrl, orNull),
      // header content background
      header_content_bg_show:            Flow(castBool, orFalse),
      header_content_bg_type:            Flow(castLower, VK(DropUnless('header_content_bg_show')), FC('solid')),
      header_content_background:         Flow(castHex, FC('#5758cf')),
      header_content_background_2:       Flow(castHex, FC('#569dd2')),
      header_content_bg_opacity:         Flow(castOpacity, FC(1.0)),
      header_content_bg_opacity_2:       Flow(castOpacity, FC(1.0)),
      header_content_solid_background:   Flow(castHex, VK(ValueUnless('header_content_bg_show', '#ffffff')), FC('#569dd2')),
      header_content_solid_bg_opacity:   Flow(castOpacity, VK(DropUnless('header_content_bg_show')), FC(1.0)),
      header_content_gradient_direction: Flow(castLower, FC('to right')),
      header_content_meta_background:    Replace(calcHeaderContentMetaBackground),
      header_content_meta_opacity:       Replace(calcHeaderContentMetaOpacity),
      // campaign button
      allow_entry:                       Flow(castBool, VE(env.props.allowEntry), orFalse),
      campaign_key:                      Flow(castCampaignKey, orNull),
      campaign_button_text:              Flow(castText, FMB('gallery_redeem_competition'), orNull),
      campaign_button_color:             Flow(castHex, FK(ColorSchema('body_background_color', 'body_bg_color_opacity', '#ffffff', '#3b3e59')), orNull),
      campaign_button_text_color:        Flow(castHex, FK(ColorSchema('campaign_button_color', '#ffffff', '#575e65')), orNull),
      campaign_focusable_close_button:   Flow(castBool, VE(env.props.campaignFocusableCloseButton), orFalse),
      // load more button
      show_load_more_button:             Flow(castBool, VK(FalseIf('carousel_mode')), FE(env.isEmbedInCommonSite), orTrue),
      load_more_button_text:             Flow(castText, FMB('gallery_load_more'), orNull),
      load_more_button_color:            Flow(castHex, FK(ColorSchema('body_background_color', 'body_bg_color_opacity', '#ffffff', '#3b3e59')), orNull),
      load_more_button_text_color:       Flow(castHex, FK(ColorSchema('load_more_button_color', '#ffffff', '#575e65')), orNull),
      load_more_button_round:            Flow(castBool, orTrue),
      // empty placeholder
      empty_placeholder_image:           Flow(castSrc, FK(ColorSchema('body_background_color', 'body_bg_color_opacity', EMPTY_IMAGE_BRIGHT, EMPTY_IMAGE_DARK)), orNull),
      empty_placeholder_text_color:      Flow(castHex, FK(ColorSchema('body_background_color', 'body_bg_color_opacity', '#ffffff', '#494949')), orNull),
      // gallery body
      body_font_name:                    Flow(castText, FC('Lato')),
      body_side_margin:                  Flow(castPosInt, VK(ValueIf('carousel_mode', 0)), FC(15)),
      // gallery background
      body_background_show:              Flow(castBool, orFalse),
      body_background_color:             Flow(castHex, VK(ValueUnless('body_background_show', '#f3f6f9')), FC('#3b3e59')),
      body_background_image:             Flow(castSrc, VK(DropUnless('body_background_show')), orNull),
      body_bg_color_opacity:             Flow(castOpacity, VK(ValueUnless('body_background_show', ValueIf('carousel_mode', 0))), VE(ValueIf(env.props.forceTransparentBodyBackground, 0)), FC(1.0)),
      body_bg_image_stretch:             Flow(castBool, orTrue),
      body_bg_apply_to_root:             Flow(castBool, VK(TrueUnless('body_background_show')), orTrue),
      // preview flow
      post_space:                        Flow(castPosInt, FC(30)),
      post_width_option:                 Flow(castPosInt, FC(1)),
      post_border_radius:                Flow(castPosInt, FC(6)),
      square_preview:                    Flow(castBool, VK(TrueIf('carousel_layout_classic')), orFalse),
      normalize_preview:                 Flow(castBool, VK(TrueIf('highlight_several_posts')), orFalse),
      show_source_in_preview:            Flow(castBool, orTrue),
      show_user_info_in_preview:         Flow(castBool, orTrue),
      show_post_desc_in_preview:         Flow(castBool, VC(FalseIf(browser.likeMobile)), orTrue),
      preview_stroke_show:               Flow(castBool, orTrue),
      preview_stroke_color:              Flow(castHex, FC('#f2f4f6')),
      // preview flow behaviour
      strict_order:                      Flow(castBool, VE(env.props.strictOrder), orFalse),
      use_raw_images_as_thumbs:          Flow(castBool, calcUseRawImagesAsThumbs, orFalse),
      scroll_to_preview_on_modal_change: Flow(castBool, VK(TrueIf('presentation_mode')), VC(FalseIf(browser.WebKit)), orFalse),
      instant_mode_until_ready:          Flow(castBool, VE(env.props.instantModeUntilReady), orFalse),
      // preview flow highlighting
      highlight_period:                  Flow(castPosOddInt, nullIfLteZero),
      highlight_all_posts:               Replace(calcHighlightAllPosts),
      highlight_several_posts:           Replace(calcHighlightSeveralPosts),
      // preview flow in carousel mode
      carousel_mode:                     Flow(castBool, VE(env.props.forceCarouselMode), orFalse),
      carousel_layout_type:              Flow(castLower, VK(DropUnless('carousel_mode')), FK(ValueIf('carousel_mode', 'classic')), orNull),
      carousel_layout_classic:           Equal('carousel_layout_type', 'classic'),
      carousel_layout_fill_viewport:     Equal('carousel_layout_type', 'fill_viewport'),
      carousel_layout_static_rows:       Equal('carousel_layout_type', 'static_rows'),
      carousel_classic_rows:             Flow(castPosInt, FC(1)),
      carousel_fill_viewport_fraction:   Flow(castPosFloat, FC(1)),
      carousel_showcase_min_height:      Flow(castPosInt, FC(350)),
      carousel_static_rows:              Flow(castPosInt, FC(1)),
      carousel_static_row_height:        Flow(castPosInt, FC(350)),
      carousel_scrollbar_show:           Flow(castBool, orTrue),
      carousel_scrollbar_size:           Flow(castPosInt, FC(9)),
      carousel_scrollbar_overlay:        Flow(castBool, calcCarouselScrollbarOverlay, orFalse),
      carousel_scrollbar_track_color:    Flow(castHex, FK(ColorSchema('body_background_color', 'body_bg_color_opacity', '#ffffff', '#000000')), orNull),
      carousel_scrollbar_thumb_color:    Flow(castHex, FK(ColorSchema('body_background_color', 'body_bg_color_opacity', '#ffffff', '#000000')), orNull),
      carousel_scrollbar_track_opacity:  Flow(castOpacity, FC(0.04)),
      carousel_scrollbar_thumb_opacity:  Flow(castOpacity, FC(0.15)),
      carousel_button_left_image:        Flow(castSrc, FK(ColorSchema('body_background_color', 'body_bg_color_opacity', CAROUSEL_BUTTON_LEFT_BRIGHT, CAROUSEL_BUTTON_LEFT_DARK)), orNull),
      carousel_button_right_image:       Flow(castSrc, FK(ColorSchema('body_background_color', 'body_bg_color_opacity', CAROUSEL_BUTTON_RIGHT_BRIGHT, CAROUSEL_BUTTON_RIGHT_DARK)), orNull),
      // custom preview grid
      custom_preview_grid_use:           Flow(castBool, VK(FalseUnless('custom_preview_grid')), orFalse),
      custom_preview_grid:               Flow(castCustomPlan, orNull),
      // custom preview width
      custom_preview_width_use:          Flow(castBool, VK(FalseUnless('custom_preview_width')), orFalse),
      custom_preview_width:              Flow(castCustomPlan, orNull),
      // custom preview height
      custom_preview_height_use:         Flow(castBool, VK(FalseUnless('custom_preview_height')), orFalse),
      custom_preview_height:             Flow(castCustomPlan, orNull),
      // adaptive static rows
      adaptive_static_rows_use:          Flow(castBool, VK(FalseUnless('adaptive_static_rows')), orFalse),
      adaptive_static_rows:              Flow(castCustomPlan, orNull),
      // modal list
      modals_use:                        Flow(castBool, CalcModalsUse(env.isIframeMode), orTrue),
      modals_show_close_button:          Flow(castBool, VK(FalseIf('presentation_auto_control')), orTrue),
      modals_bg_show:                    Flow(castBool, orFalse),
      modals_bg_mobile_color:            Flow(castHex, VK(ValueUnless('modals_bg_show', '#1d3249')), FK('modals_bg_desktop_color'), orNull),
      modals_bg_desktop_color:           Flow(castHex, VK(ValueUnless('modals_bg_show', '#020810')), FC('#3b3e59')),
      modals_bg_desktop_opacity:         Flow(castOpacity, VK(ValueUnless('modals_bg_show', 0.65)), FC(1.0)),
      modals_fix_fixed_position:         Flow(castBool, FC(isFixedPositionBroken($portalElement))),
      modals_use_continuous_switching:   Flow(castBool, VK(FalseIf('presentation_auto_control')), VC(FalseIf(browser.likeIE, browser.WebKit)), orTrue),
      modals_focusable_close_button:     Flow(castBool, VK(FalseIf('modals_show_close_button')), VE(env.props.modalsFocusableCloseButton), orFalse),
      // modal
      modal_force_vertical_layout:       Flow(castBool, orFalse),
      modal_attach_branding_footer:      Flow(castBool, orFalse),
      modal_show_branding_footer:        Flow(castBool, calcModalShowBrandingFooter, orTrue),
      modal_show_switch_buttons:         Flow(castBool, VK(FalseIf('presentation_auto_control')), orTrue),
      modal_border_radius:               Flow(castPosInt, FC(4)),
      modal_show_shadow:                 Flow(castBool, orFalse),
      modal_show_placeholder:            Flow(castBool, orTrue),
      show_source_in_modal:              Flow(castBool, orTrue),
      show_user_info_in_modal:           Flow(castBool, orTrue),
      show_post_desc_in_modal:           Flow(castBool, orTrue),
      // slider
      slider_force_tiny:                 Flow(castBool, orFalse),
      slider_show_switch_buttons:        Flow(castBool, VK(FalseIf('presentation_auto_control')), orTrue),
      slider_show_position_indicator:    Flow(castBool, VK(FalseIf('presentation_auto_control')), orTrue),
      // text item
      text_force_tiny:                   Flow(castBool, orFalse),
      // limits
      top_posts_limit:                   Flow(castPosInt, calcTopPostsLimit, nullIfLteZero),
      above_posts_limit:                 Flow(castPosInt, calcAbovePostsLimit, nullIfLteZero),
      below_posts_limit:                 Flow(castPosInt, calcBelowPostsLimit, nullIfLteZero),
      global_posts_limit:                Flow(castPosInt, nullIfLteZero)
    }

    const obj = debugFields({
      keys: keys(key2fix),
      ovr: {},
      raw: {},
      get: getProp,
      has: hasProp,
      set: setProps,
      override: overrideProps,
      unoverride: unoverrideProps
    }, {
      _fix: fixProps,
      _input: input,
      _key2fix: key2fix,
      _calcProp: calcProp,
      _cloneProps: cloneProps
    })

    assign(obj, {
      key2set: StrictObj(zipObject(obj.keys, obj.keys.map(KeySetter))),
      key2emitter: StrictObj(zipObject(obj.keys, obj.keys.map(KeyEmitter))),
      key2override: StrictObj(zipObject(obj.keys, obj.keys.map(KeyOverrider)))
    })

    if (isProperties(input)) {
      cloneProps(input)
    } else if (isNotEmptyObject(input)) {
      setProps(input)
    } else {
      fixProps()
    }

    return LockableObj(attachGetters(StrictObj(obj), 'key2get')).lockWrite()

    function KeyEmitter(key) {
      return Emitter(`properties::${key}`)
    }

    function KeySetter(key) {
      return function setKey(val) {
        return setProps(key, val)
      }
    }

    function KeyOverrider(key) {
      return function overrideKey(val) {
        return overrideProps(key, val)
      }
    }

    function overrideProps(arg1, arg2) {
      const props = isString(arg1) ? { [arg1]: arg2 } : arg1

      let changed = false
      for (const [key, next] of toPairs(props)) {
        const equal = obj.ovr.hasOwnProperty(key) && isEqual(obj.ovr[key], next)
        if (!equal) {
          obj.ovr[key] = next
          changed = true
        }
      }

      if (changed) {
        return fixProps()
      }
    }

    function unoverrideProps(...keys) {
      let changed = false
      for (const key of keys) {
        const exist = obj.ovr.hasOwnProperty(key)
        if (exist) {
          delete obj.ovr[key]
          changed = true
        }
      }

      if (changed) {
        return fixProps()
      }
    }

    function getProp(key) {
      return obj[key]
    }

    function hasProp(key) {
      return includes(obj.keys, key)
    }

    function cloneProps(orig) {
      obj.raw = cloneDeep(orig.raw)
      obj.ovr = cloneDeep(orig.ovr)

      for (const key of obj.keys) {
        obj[key] = cloneDeep(orig[key])
      }

      for (const key of obj.keys) {
        obj.key2emitter[key].emit(obj[key])
      }

      return obj.keys.concat()
    }

    function setProps(arg1, arg2) {
      const props = isString(arg1) ? { [arg1]: arg2 } : arg1

      let changed = false
      for (const [key, next] of toPairs(props)) {
        const prev = obj.raw[key]
        if (!isEqual(prev, next)) {
          obj.raw[key] = next
          changed = true
        }
      }

      if (changed) {
        return fixProps()
      }
    }

    function fixProps() {
      const oldObj = clone(obj)
      const calcPropCache = {}

      for (const key of obj.keys) {
        const prev = obj[key]
        const next = calcProp(calcPropCache, key)
        if (!isObject(prev) || !isObject(next) || !isEqual(prev, next)) {
          obj[key] = next
        }
      }

      const diff = topDiff(oldObj, obj)
      printPropertiesDiff(diff)

      for (const chunk of diff) {
        const key = chunk.path[0]
        const val = chunk.rhs
        const keyEmitter = obj.key2emitter[key]
        keyEmitter.emit(val)
      }

      const changedKeys = diff.map((chunk) => chunk.path[0])
      return changedKeys
    }

    function calcProp(cache, key) {
      if (has(cache, key)) {
        return cache[key]
      }

      const rawVal = obj.ovr.hasOwnProperty(key)
        ? obj.ovr[key]
        : obj.raw[key]

      try {
        const objVal = key2fix[key](rawVal, wrap(cache, calcProp))
        return cache[key] = objVal
      } catch (err) {
        $log.warn(`Properties#calcProp: can not calculate [key=${key}]!`, err)
        throw new Error(`Properties#calcProp: can not calculate [key=${key}] -> ${err2str(err)}!`)
      }
    }
  }
}
